import {
  type FormatPrefs,
  formatDayShort,
  formatMonthDay,
  formatNights,
  formatTime,
  plural,
} from './format';
import { distanceMi, type LatLon } from './geo';
import {
  cityOf,
  isMoveType,
  type PlaceLookup,
  rentalDays,
  spanNights,
  stayName,
  stayShortName,
} from './items';
import type { Item, Place } from './schema';
import { effectiveTimes, type Span as Span2 } from './sequence';
import { addDays, datesBetween, diffDays, instantFromLocal, localDate, tzAbbr } from './time';

/**
 * Builds the trip timeline: one continuous path of events, with stays and car rentals as
 * spans in their own lanes, quiet days folded into one line, and today marked.
 */

export type LaneMark = 'none' | 'start' | 'full' | 'end' | 'one';

export interface Lane {
  kind: 'stay' | 'car';
  /** Item ids of the spans assigned to this lane, in time order. */
  itemIds: string[];
}

export type EventKind =
  | 'depart'
  | 'arrive'
  | 'checkin'
  | 'checkout'
  | 'pickup'
  | 'dropoff'
  | 'single';

interface RowBase {
  key: string;
  /** One mark per lane, same order as `TimelineModel.lanes`. */
  lanes: LaneMark[];
}

export interface DayRow extends RowBase {
  kind: 'day';
  date: string;
  dayNumber: number;
  label: string;
  isToday: boolean;
}

export interface EventRow extends RowBase {
  kind: 'event';
  itemId: string;
  itemType: Item['type'];
  event: EventKind;
  at: number | null;
  tz: string | null;
  date: string;
  time: string | null;
  /** Zone abbreviation, present only where the zone changes. */
  tzLabel: string | null;
  title: string;
  subtitle: string | null;
  cancelled: boolean;
  hasNotes: boolean;
}

export interface FoldRow extends RowBase {
  kind: 'fold';
  fromDate: string;
  toDate: string;
  title: string;
  subtitle: string | null;
  isToday: boolean;
}

export type TimelineRow = DayRow | EventRow | FoldRow;

export interface TimelineModel {
  lanes: Lane[];
  rows: TimelineRow[];
  startDate: string | null;
  endDate: string | null;
  /** Plans with no date yet. */
  unscheduled: Item[];
}

interface TimedEvent {
  item: Item;
  event: EventKind;
  at: number | null;
  /** When it really happens in the day's order (see sequence.ts); `at` is the booked time. */
  sortAt: number;
  tz: string;
  date: string;
  order: number;
}

interface Span {
  item: Item;
  kind: 'stay' | 'car';
  start: number;
  end: number;
  startDate: string;
  endDate: string;
}

const EVENT_ORDER: Record<EventKind, number> = {
  checkout: 0,
  dropoff: 1,
  arrive: 2,
  single: 3,
  depart: 4,
  pickup: 5,
  checkin: 6,
};

/**
 * A day's check-outs come before its next check-in, even when the old hotel's late check-out
 * is later than the new one's check-in: that's the order the day is lived in.
 */
function checkoutsFirst(events: TimedEvent[]) {
  for (let i = 0; i < events.length; i++) {
    const e = events[i]!;
    if (e.event !== 'checkout') continue;
    let to = i;
    for (let j = i - 1; j >= 0 && events[j]!.date === e.date; j--) {
      if (events[j]!.item.id === e.item.id) break;
      if (events[j]!.event === 'checkin') to = j;
    }
    if (to < i) events.splice(to, 0, ...events.splice(i, 1));
  }
}

function eventsOf(item: Item, actual?: Span2): TimedEvent[] {
  if (item.startAt == null || !item.startTz) return [];
  const startTz = item.startTz;
  const endTz = item.endTz ?? startTz;
  const out: TimedEvent[] = [];
  const push = (event: EventKind, at: number, tz: string) => {
    // Starts and ends that only make sense in another order (a car returned "after" the
    // flight home) sort, and land on the day, where they really happen.
    const isEnd = event === 'arrive' || event === 'checkout' || event === 'dropoff';
    const sortAt = (isEnd ? actual?.end : actual?.start) ?? at;
    out.push({
      item,
      event,
      at: item.allDay ? null : at,
      sortAt,
      tz,
      date: localDate(sortAt, tz),
      order: EVENT_ORDER[event],
    });
  };
  if (isMoveType(item.type)) {
    push('depart', item.startAt, startTz);
    if (item.endAt != null) push('arrive', item.endAt, endTz);
  } else if (item.type === 'lodging') {
    push('checkin', item.startAt, startTz);
    if (item.endAt != null) push('checkout', item.endAt, endTz);
  } else if (item.type === 'car') {
    push('pickup', item.startAt, startTz);
    if (item.endAt != null) push('dropoff', item.endAt, endTz);
  } else {
    push('single', item.startAt, startTz);
  }
  return out;
}

function eventTitle(e: TimedEvent, places: PlaceLookup, isHome: (p?: Place) => boolean): string {
  const { item } = e;
  const d = item.details;
  switch (e.event) {
    case 'depart': {
      const dest = places(item.endPlaceId);
      const city = dest?.city;
      if (isHome(dest)) return item.type === 'flight' ? 'Fly home' : 'Head home';
      if (item.type === 'flight')
        return city ? `Fly to ${city}` : `Depart ${places(item.startPlaceId)?.iata ?? ''}`.trim();
      const verb =
        item.type === 'cruise'
          ? 'Sail'
          : item.type === 'ferry'
            ? 'Ferry'
            : item.type === 'bus'
              ? 'Bus'
              : 'Train';
      return city ? `${verb} to ${city}` : item.title;
    }
    case 'arrive': {
      const p = places(item.endPlaceId);
      if (item.type === 'flight') return `Land at ${p?.iata ?? p?.name ?? 'destination'}`;
      return `Arrive in ${p?.city ?? p?.name ?? 'destination'}`;
    }
    case 'checkin':
      return `Check in, ${stayName(item)}`;
    case 'checkout':
      return `Check out, ${stayName(item)}`;
    case 'pickup':
      return `Pick up ${d.type === 'car' && d.company ? `${d.company} ` : ''}car`;
    case 'dropoff':
      return `Return ${d.type === 'car' && d.company ? `${d.company} ` : ''}car`;
    default:
      return item.title;
  }
}

function eventSubtitle(
  e: TimedEvent,
  places: PlaceLookup,
  prefs: FormatPrefs,
  isHome: (p?: Place) => boolean,
): string | null {
  const { item } = e;
  const d = item.details;
  const parts: string[] = [];
  switch (e.event) {
    case 'depart':
      if (d.type === 'flight') {
        parts.push(item.title);
        if (d.seat) parts.push(`seat ${d.seat}`);
        if (item.confirmation) parts.push(`confirmation ${item.confirmation}`);
      } else {
        parts.push(item.title);
      }
      break;
    case 'arrive': {
      const p = places(item.endPlaceId);
      if (
        item.endAt != null &&
        item.startAt != null &&
        item.endTz &&
        e.date !== localDate(item.startAt, item.startTz ?? item.endTz)
      ) {
        parts.push('Next day');
      }
      if (isHome(p)) parts.push('Home');
      else if (p?.city && item.type === 'flight') parts.push(p.city);
      break;
    }
    case 'checkin': {
      const n = spanNights(item);
      if (n > 0) parts.push(plural(n, 'night'));
      break;
    }
    case 'checkout':
      if (places(item.startPlaceId)?.city) parts.push(places(item.startPlaceId)!.city!);
      break;
    case 'pickup': {
      const days = rentalDays(item);
      if (days > 0) parts.push(plural(days, 'day'));
      const ret = places(item.endPlaceId ?? item.startPlaceId);
      if (ret) parts.push(`return at ${ret.iata ?? ret.name}`);
      break;
    }
    case 'dropoff': {
      const p = places(item.endPlaceId ?? item.startPlaceId);
      if (p) parts.push(p.iata ? `${p.iata} rental return` : p.name);
      break;
    }
    default: {
      if (item.endAt != null && item.endTz && !item.allDay) {
        parts.push(`Until ${formatTime(item.endAt, item.endTz, prefs)}`);
      }
      const p = places(item.startPlaceId);
      if (p && p.name !== item.title) parts.push(p.name);
    }
  }
  if (parts.length === 0) return null;
  const s = parts.join(', ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Assigns spans to lanes so overlapping spans of the same kind never share a lane. */
function assignLanes(spans: Span[]): { lanes: Lane[]; laneOf: Map<string, number> } {
  const lanes: (Lane & { lastEnd: number })[] = [];
  const laneOf = new Map<string, number>();
  for (const kind of ['stay', 'car'] as const) {
    const ofKind = spans.filter((s) => s.kind === kind).sort((a, b) => a.start - b.start);
    for (const s of ofKind) {
      let idx = lanes.findIndex((l) => l.kind === kind && l.lastEnd <= s.start);
      if (idx < 0) {
        lanes.push({ kind, itemIds: [], lastEnd: -Infinity });
        idx = lanes.length - 1;
      }
      const lane = lanes[idx]!;
      lane.itemIds.push(s.item.id);
      lane.lastEnd = s.end;
      laneOf.set(s.item.id, idx);
    }
  }
  return { lanes: lanes.map(({ kind, itemIds }) => ({ kind, itemIds })), laneOf };
}

export interface TimelineOptions {
  places: PlaceLookup;
  prefs: FormatPrefs;
  /** Today's local date where I am, to mark today. */
  today?: string;
  /** Trip date overrides. */
  startDate?: string | null;
  endDate?: string | null;
  /** Home base, so the way back reads "Fly home". */
  home?: (LatLon & { radiusMi: number }) | null;
}

export function buildTimeline(items: Item[], opts: TimelineOptions): TimelineModel {
  const { places, prefs } = opts;
  const home = opts.home;
  const isHome = (p?: Place): boolean =>
    !!home &&
    p?.lat != null &&
    p.lon != null &&
    distanceMi(home, { lat: p.lat, lon: p.lon }) <= home.radiusMi;
  const live = items.filter((i) => !i.deleted);
  const unscheduled = live.filter((i) => i.startAt == null || !i.startTz);
  const actual = effectiveTimes(live, places);
  const events = live
    .flatMap((i) => eventsOf(i, actual.get(i.id)))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (a.at == null || b.at == null ? 0 : a.sortAt - b.sortAt) ||
        a.order - b.order,
    );
  checkoutsFirst(events);

  const spans: Span[] = live
    .filter((i) => (i.type === 'lodging' || i.type === 'car') && i.status === 'active')
    .filter((i) => i.startAt != null && i.endAt != null && i.startTz)
    .map((i) => ({
      item: i,
      kind: i.type === 'lodging' ? ('stay' as const) : ('car' as const),
      start: i.startAt!,
      end: i.endAt!,
      startDate: localDate(i.startAt!, i.startTz!),
      endDate: localDate(i.endAt!, i.endTz ?? i.startTz!),
    }));
  const { lanes, laneOf } = assignLanes(spans);
  const spanById = new Map(spans.map((s) => [s.item.id, s]));

  let startDate = events[0]?.date ?? null;
  let endDate = events[events.length - 1]?.date ?? null;
  if (opts.startDate) startDate = opts.startDate;
  if (opts.endDate) endDate = opts.endDate;
  if (!startDate || !endDate || endDate < startDate) {
    return { lanes, rows: [], startDate, endDate, unscheduled };
  }

  /** Lane marks for a moment in time, optionally for the event row of a span. */
  const marksAt = (t: number, ownEvent?: TimedEvent): LaneMark[] =>
    lanes.map((lane) => {
      for (const id of lane.itemIds) {
        const s = spanById.get(id)!;
        if (ownEvent && ownEvent.item.id === id) {
          if (s.start === s.end) return 'one';
          return ownEvent.event === 'checkin' || ownEvent.event === 'pickup' ? 'start' : 'end';
        }
        if (t > s.start && t < s.end) return 'full';
      }
      return 'none';
    });

  const eventsByDate = new Map<string, TimedEvent[]>();
  for (const e of events) {
    const list = eventsByDate.get(e.date) ?? [];
    list.push(e);
    eventsByDate.set(e.date, list);
  }

  /** Representative instant for a date: local noon in the zone of nearby events. */
  let lastTz = events[0]?.tz ?? 'UTC';
  const tzForDate = new Map<string, string>();
  for (const date of datesBetween(startDate, endDate)) {
    const evs = eventsByDate.get(date);
    if (evs?.length) lastTz = evs[evs.length - 1]!.tz;
    tzForDate.set(date, lastTz);
  }
  const noon = (date: string) => instantFromLocal(`${date}T12:00`, tzForDate.get(date) ?? 'UTC');
  const midnight = (date: string) => instantFromLocal(date, tzForDate.get(date) ?? 'UTC');

  /** Which stay covers the night of `date`. */
  const stayForNight = (date: string): Span | undefined =>
    spans.find((s) => s.kind === 'stay' && s.startDate <= date && date < s.endDate);

  const rows: TimelineRow[] = [];
  let shownTz: string | null = null;
  const today = opts.today;

  const pushDay = (date: string) => {
    rows.push({
      kind: 'day',
      key: `day-${date}`,
      date,
      dayNumber: diffDays(startDate!, date) + 1,
      label: formatDayShort(date, prefs),
      isToday: date === today,
      lanes: marksAt(midnight(date)),
    });
  };

  const foldText = (
    from: string,
    to: string,
    isToday: boolean,
  ): { title: string; subtitle: string | null } => {
    const stay = stayForNight(from);
    const sameStay = stay && datesBetween(from, to).every((d) => stayForNight(d) === stay);
    const nightOf = (d: string) => diffDays(stay!.startDate, d) + 1;
    if (isToday) {
      if (stay) {
        return {
          title: `${formatNights(nightOf(from), nightOf(from), diffDays(stay.startDate, stay.endDate))} at ${stayShortName(stay.item)}`,
          subtitle: 'Nothing else planned',
        };
      }
      return { title: 'Nothing planned today', subtitle: null };
    }
    const range =
      from === to
        ? formatMonthDay(from, prefs)
        : `${formatMonthDay(from, prefs)} to ${shortEnd(from, to)}`;
    if (stay && sameStay) {
      return {
        title: `${range}, staying at ${stayShortName(stay.item)}`,
        subtitle: formatNights(nightOf(from), nightOf(to), diffDays(stay.startDate, stay.endDate)),
      };
    }
    return { title: range, subtitle: 'Nothing planned' };
  };

  const shortEnd = (from: string, to: string): string => {
    // 'Oct 4 to 9' when in the same month, else 'Sep 29 to Oct 2'.
    return from.slice(0, 7) === to.slice(0, 7)
      ? String(Number(to.slice(8, 10)))
      : formatMonthDay(to, prefs);
  };

  const pushFold = (from: string, to: string, isToday: boolean) => {
    const t = foldText(from, to, isToday);
    rows.push({
      kind: 'fold',
      key: `fold-${from}`,
      fromDate: from,
      toDate: to,
      title: t.title,
      subtitle: t.subtitle,
      isToday,
      lanes: marksAt(noon(from)),
    });
  };

  const dates = datesBetween(startDate, endDate);
  let foldStart: string | null = null;
  const flushFold = (endBefore: string) => {
    if (foldStart) {
      pushFold(foldStart, addDays(endBefore, -1), false);
      foldStart = null;
    }
  };

  for (const date of dates) {
    const evs = eventsByDate.get(date) ?? [];
    const isToday = date === today;
    if (evs.length === 0 && !isToday) {
      foldStart ??= date;
      continue;
    }
    flushFold(date);
    pushDay(date);
    if (evs.length === 0) {
      pushFold(date, date, true);
      continue;
    }
    for (const e of evs) {
      const showTz = e.at != null && e.tz !== shownTz;
      if (e.at != null) shownTz = e.tz;
      rows.push({
        kind: 'event',
        key: `ev-${e.item.id}-${e.event}`,
        itemId: e.item.id,
        itemType: e.item.type,
        event: e.event,
        at: e.at,
        tz: e.tz,
        date: e.date,
        time: e.at != null ? formatTime(e.at, e.tz, prefs) : null,
        tzLabel: showTz && e.at != null ? tzAbbr(e.at, e.tz) : null,
        title: eventTitle(e, places, isHome),
        subtitle: eventSubtitle(e, places, prefs, isHome),
        cancelled: e.item.status === 'cancelled',
        hasNotes: e.item.notes.trim().length > 0,
        lanes:
          e.item.status === 'active' && laneOf.has(e.item.id)
            ? marksAt(e.at ?? noon(e.date), e)
            : marksAt(e.at ?? noon(e.date)),
      });
    }
  }
  if (foldStart) pushFold(foldStart, endDate, false);

  return { lanes, rows, startDate, endDate, unscheduled };
}

/** City names in visit order, for headers like 'Minneapolis and Bloomington'. */
export function citiesInOrder(items: Item[], places: PlaceLookup, home?: string | null): string[] {
  const seen: string[] = [];
  for (const i of [...items].sort((a, b) => (a.startAt ?? 0) - (b.startAt ?? 0))) {
    const c = cityOf(i, places);
    if (c && c !== home && !seen.includes(c)) seen.push(c);
  }
  return seen;
}
